import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DateInput from "./DateInput";
import { formatTelephone } from "../utils/formatters";
import "./RechercheModal.css";
import VilleAutocomplete from "./VilleAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRID_COLS = [
  ["noclient", "No client"],
  ["nom", "Nom"],
  ["prenom", "Prénom"],
  ["date_naissance", "Date nais."],
  ["nas", "N.A.S."],
  ["adresse", "Adresse"],
  ["ville", "Ville"],
  ["code_postal", "Code postal"],
  ["telephone", "Tél"],
  ["cellulaire", "Cellulaire"],
  ["courriel", "Courriel"],
];

function ClientsGrid({ rows, selectedIdx, onSelect, onDouble, testid, emptyMsg }) {
  if (!rows.length) {
    return <div style={{ padding: "10px 12px", color: "#666", fontSize: 12 }} data-testid={`${testid}-empty`}>{emptyMsg}</div>;
  }
  return (
    <div className="rech-table-wrap" style={{ maxHeight: 180 }}>
      <table className="rech-table" data-testid={testid}>
        <thead>
          <tr>{GRID_COLS.map(([k, l]) => <th key={k}>{l}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.noclient}
                className={i === selectedIdx ? "rech-row-selected" : ""}
                onClick={() => onSelect(i)}
                onDoubleClick={() => onDouble && onDouble(i)}
                data-testid={`${testid}-row-${i}`}>
              {GRID_COLS.map(([k]) => <td key={k}>{r[k] || ""}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BADGE_YES = { fontSize: 10, fontWeight: 700, color: "#0a6b0a", background: "#e6f4e6", border: "1px solid #b8dcb8", borderRadius: 3, padding: "0 5px", whiteSpace: "nowrap" };
const BADGE_NO = { fontSize: 10, fontWeight: 700, color: "#8a6d1f", background: "#fdf6e3", border: "1px solid #e6d9a8", borderRadius: 3, padding: "0 5px", whiteSpace: "nowrap" };

function Champ({ label, children, width, daj }) {
  return (
    <div className="rech-field" style={width ? { maxWidth: width } : undefined}>
      <label style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span>{label}</span>
        {daj === true && <span style={BADGE_YES} title="Ce champ est propagé à TOUTES les demandes (DAJ) du client, y compris les demandes fermées.">↳ MàJ demandes</span>}
        {daj === false && <span style={BADGE_NO} title="Ce champ met à jour la fiche client / l'adresse, mais N'EST PAS propagé aux demandes (DAJ) existantes.">demandes : non</span>}
      </label>
      {children}
    </div>
  );
}

export default function JumelageModal({ onClose }) {
  const [tab, setTab] = useState("fusion");           // fusion | journal
  const [step, setStep] = useState("recherche");      // recherche | coord | resultat
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [candidats, setCandidats] = useState([]);
  const [jumeles, setJumeles] = useState([]);
  const [selCand, setSelCand] = useState(-1);
  const [selJum, setSelJum] = useState(-1);

  // Étape coordonnées
  const [coord, setCoord] = useState(null);
  const [categories, setCategories] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [resultat, setResultat] = useState(null);

  // Journal
  const [logs, setLogs] = useState([]);
  const [logDetail, setLogDetail] = useState(null);

  const conserve = selJum >= 0 ? jumeles[selJum] : null;

  const handleSearch = useCallback(async () => {
    setError("");
    if (!nom.trim() && !prenom.trim()) {
      setError("Entrez au moins un nom ou un prénom.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/jumelage/recherche`, { nom, prenom });
      const dejaJumeles = new Set(jumeles.map(j => j.noclient));
      setCandidats(res.data.filter(r => !dejaJumeles.has(r.noclient)));
      setSelCand(-1);
      if (res.data.length === 0) setError("Pas de client de ce nom.");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [nom, prenom, jumeles]);

  const ajouter = () => {
    if (selCand < 0) return;
    const c = candidats[selCand];
    setJumeles(prev => [...prev, c]);
    setCandidats(prev => prev.filter((_, i) => i !== selCand));
    setSelCand(-1);
  };

  const enlever = () => {
    if (selJum < 0) return;
    const c = jumeles[selJum];
    setCandidats(prev => [...prev, c]);
    setJumeles(prev => prev.filter((_, i) => i !== selJum));
    setSelJum(-1);
  };

  const ouvrirCoordonnees = async () => {
    if (!conserve) return;
    setError("");
    setLoading(true);
    try {
      const [cli, cats, provs] = await Promise.all([
        axios.get(`${API}/jumelage/client/${encodeURIComponent(conserve.noclient)}`),
        axios.get(`${API}/reference/categories`),
        axios.get(`${API}/reference/provinces`),
      ]);
      setCoord({
        ...cli.data,
        // Nettoie les masques legacy "(418) ___-____" : chiffres seuls, reformatés
        tel_res: formatTelephone(cli.data.tel_res),
        tel_bur: formatTelephone(cli.data.tel_bur),
        cellulaire: formatTelephone(cli.data.cellulaire),
      });
      setCategories(cats.data || []);
      setProvinces(provs.data || []);
      setStep("coord");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const executer = async () => {
    setConfirming(false);
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/jumelage/executer`, {
        noclient_conserve: coord.noclient,
        clients_fusionnes: jumeles.map(j => j.noclient),
        coordonnees: coord,
      });
      setResultat({ ...res.data, coordonnees: { ...coord } });
      setStep("resultat");
      setJumeles([]);
      setCandidats([]);
      setSelJum(-1);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const chargerLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jumelage/logs`);
      setLogs(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => {
    if (tab === "journal") { setLogDetail(null); chargerLogs(); }
  }, [tab, chargerLogs]);

  const ouvrirLog = async (id) => {
    try {
      const res = await axios.get(`${API}/jumelage/logs/${id}`);
      setLogDetail(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const up = (k, v) => setCoord(prev => ({ ...prev, [k]: v }));
  const nbJum = jumeles.length;
  const btnLabel = nbJum > 1 ? "Jumeler les clients" : "Mettre à jour les coordonnées";

  return (
    <div className="rech-overlay">
      <div className="rech-modal" data-testid="jumelage-modal" style={{ width: "min(1200px, 96vw)" }}>
        <div className="rech-titlebar">
          <span>Jumelage de clients {step === "coord" && coord ? `— ${coord.prenom} ${coord.nom} (${coord.noclient})` : ""}</span>
          <button className="rech-close" onClick={onClose} data-testid="jumelage-close" title="Fermer">×</button>
        </div>

        {/* Onglets */}
        <div style={{ display: "flex", gap: 4, padding: "8px 12px 0" }}>
          <button className={tab === "fusion" ? "rech-btn-primary" : "rech-btn-secondary"}
                  onClick={() => setTab("fusion")} data-testid="jumelage-tab-fusion">Jumelage</button>
          <button className={tab === "journal" ? "rech-btn-primary" : "rech-btn-secondary"}
                  onClick={() => setTab("journal")} data-testid="jumelage-tab-journal">Journal</button>
        </div>

        <div className="rech-body" style={{ display: "flex", flexDirection: "column" }}>
          {error && <div className="rech-error" data-testid="jumelage-error" style={{ marginBottom: 8 }}>{error}</div>}

          {tab === "fusion" && step === "recherche" && (
            <>
              <div className="rech-criteres">
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <Champ label="Nom" width={220}>
                    <input value={nom} onChange={e => setNom(e.target.value)}
                           onKeyDown={e => e.key === "Enter" && handleSearch()}
                           data-testid="jumelage-nom" autoFocus />
                  </Champ>
                  <Champ label="Prénom" width={220}>
                    <input value={prenom} onChange={e => setPrenom(e.target.value)}
                           onKeyDown={e => e.key === "Enter" && handleSearch()}
                           data-testid="jumelage-prenom" />
                  </Champ>
                  <button className="rech-btn-primary" onClick={handleSearch} disabled={loading}
                          data-testid="jumelage-btn-recherche">
                    {loading ? "Recherche…" : "Rechercher"}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 10, fontWeight: 600, fontSize: 12 }}>Clients trouvés</div>
              <ClientsGrid rows={candidats} selectedIdx={selCand} onSelect={setSelCand}
                           onDouble={(i) => { setSelCand(i); }} testid="jumelage-grid-candidats"
                           emptyMsg="Lancez une recherche pour afficher les clients." />

              <div style={{ display: "flex", gap: 8, justifyContent: "center", margin: "8px 0" }}>
                <button className="rech-btn-secondary" onClick={ajouter} disabled={selCand < 0}
                        data-testid="jumelage-btn-ajouter">▼ Ajouter au jumelage</button>
                <button className="rech-btn-secondary" onClick={enlever} disabled={selJum < 0}
                        data-testid="jumelage-btn-enlever">▲ Enlever</button>
              </div>

              <div style={{ fontWeight: 600, fontSize: 12 }}>
                Clients à jumeler {nbJum > 0 && <span style={{ fontWeight: 400 }}>
                  — sélectionnez la ligne dont les informations seront <u>conservées</u></span>}
              </div>
              <ClientsGrid rows={jumeles} selectedIdx={selJum} onSelect={setSelJum}
                           testid="jumelage-grid-jumeles"
                           emptyMsg="Aucun client sélectionné pour le jumelage." />

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                <button className="rech-btn-secondary" onClick={onClose} data-testid="jumelage-btn-fermer">Fermer</button>
                {nbJum > 0 && (
                  <button className="rech-btn-primary" onClick={ouvrirCoordonnees}
                          disabled={selJum < 0 || loading}
                          title={selJum < 0 ? "Sélectionnez le client à conserver" : ""}
                          data-testid="jumelage-btn-continuer">
                    {btnLabel} →
                  </button>
                )}
              </div>
            </>
          )}

          {tab === "fusion" && step === "coord" && coord && (
            <>
              <div style={{ fontSize: 12, marginBottom: 8, color: "#333" }}>
                Vérifiez / corrigez les coordonnées qui seront conservées pour le client
                <strong> {coord.noclient}</strong>
                {nbJum > 1 && <> — les {nbJum - 1} autre(s) client(s) seront fusionnés puis supprimés.</>}
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 11,
                            background: "#f4f7fb", border: "1px solid #d5e0ee", borderRadius: 4, padding: "6px 10px", marginBottom: 10 }}
                   data-testid="jumelage-legende-daj">
                <span><span style={BADGE_YES}>↳ MàJ demandes</span> = propagé à toutes les demandes (DAJ) du client, <b>même fermées</b></span>
                <span><span style={BADGE_NO}>demandes : non</span> = met à jour la fiche client/adresse seulement, <b>pas</b> les demandes</span>
              </div>
              <div className="rech-cols">
                <div className="rech-col">
                  <Champ label="Nom" daj={true}><input value={coord.nom || ""} onChange={e => up("nom", e.target.value)} data-testid="jumelage-coord-nom" /></Champ>
                  <Champ label="Prénom" daj={true}><input value={coord.prenom || ""} onChange={e => up("prenom", e.target.value)} data-testid="jumelage-coord-prenom" /></Champ>
                  <Champ label="Date de naissance" daj={true}>
                    <DateInput value={coord.date_naissance || ""} onChange={v => up("date_naissance", v)} testId="jumelage-coord-ddn" style={{ width: "100%" }} />
                  </Champ>
                  <Champ label="N.A.S." daj={true}><input value={coord.nas || ""} maxLength={11} onChange={e => up("nas", e.target.value)} data-testid="jumelage-coord-nas" /></Champ>
                  <Champ label="Sexe" daj={true}>
                    <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                      <label><input type="radio" checked={coord.sexe === 0} onChange={() => up("sexe", 0)} data-testid="jumelage-coord-sexe-f" /> Féminin</label>
                      <label><input type="radio" checked={coord.sexe === 1} onChange={() => up("sexe", 1)} data-testid="jumelage-coord-sexe-m" /> Masculin</label>
                    </div>
                  </Champ>
                  <Champ label="Langue" daj={false}>
                    <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                      <label><input type="radio" checked={coord.langue === 1} onChange={() => up("langue", 1)} data-testid="jumelage-coord-langue-fr" /> Français</label>
                      <label><input type="radio" checked={coord.langue === 0} onChange={() => up("langue", 0)} data-testid="jumelage-coord-langue-an" /> Anglais</label>
                    </div>
                  </Champ>
                </div>
                <div className="rech-col">
                  <Champ label="Catégorie de requérant" daj={false}>
                    <select value={coord.categorie || ""} onChange={e => up("categorie", e.target.value)} data-testid="jumelage-coord-categorie">
                      <option value=""></option>
                      {categories.map(c => (
                        <option key={c.code || c.description} value={c.description}>{c.description}</option>
                      ))}
                    </select>
                  </Champ>
                  <Champ label="No bande" daj={false}><input value={coord.no_bande || ""} maxLength={20} onChange={e => up("no_bande", e.target.value)} data-testid="jumelage-coord-bande" /></Champ>
                  <Champ label="No S.R." daj={false}><input value={coord.nsr || ""} maxLength={12} onChange={e => up("nsr", e.target.value)} data-testid="jumelage-coord-nsr" /></Champ>
                  <Champ label="No SED" daj={true}><input value={coord.sed || ""} maxLength={10} onChange={e => up("sed", e.target.value)} data-testid="jumelage-coord-sed" /></Champ>
                  <Champ label="Région éloignée" daj={false}>
                    <label style={{ fontSize: 12 }}>
                      <input type="checkbox" checked={coord.reg_eloigne === 1}
                             onChange={e => up("reg_eloigne", e.target.checked ? 1 : 0)}
                             data-testid="jumelage-coord-regeloign" /> Oui
                    </label>
                  </Champ>
                </div>
                <div className="rech-col">
                  <Champ label="Adresse" daj={false}><input value={coord.adresse || ""} maxLength={40} onChange={e => up("adresse", e.target.value)} data-testid="jumelage-coord-adresse" /></Champ>
                  <Champ label="Ville" daj={false}><VilleAutocomplete value={coord.ville || ""} maxLength={40} onChange={v => up("ville", v)} className="" testId="jumelage-coord-ville" /></Champ>
                  <Champ label="Province" daj={false}>
                    <select value={coord.province || ""} onChange={e => up("province", e.target.value)} data-testid="jumelage-coord-province">
                      <option value=""></option>
                      {provinces.map(p => (
                        <option key={p.code} value={p.code}>{p.code}</option>
                      ))}
                    </select>
                  </Champ>
                  <Champ label="Code postal" daj={false}><input value={coord.code_postal || ""} maxLength={7} onChange={e => up("code_postal", e.target.value)} data-testid="jumelage-coord-cp" /></Champ>
                </div>
                <div className="rech-col">
                  <Champ label="Téléphone (rés.)" daj={false}><input value={formatTelephone(coord.tel_res)} maxLength={14} onChange={e => up("tel_res", formatTelephone(e.target.value))} placeholder="418-123-4567" data-testid="jumelage-coord-telres" /></Champ>
                  <Champ label="Téléphone (bur.)" daj={false}><input value={formatTelephone(coord.tel_bur)} maxLength={14} onChange={e => up("tel_bur", formatTelephone(e.target.value))} placeholder="418-123-4567" data-testid="jumelage-coord-telbur" /></Champ>
                  <Champ label="Cellulaire" daj={false}><input value={formatTelephone(coord.cellulaire)} maxLength={14} onChange={e => up("cellulaire", formatTelephone(e.target.value))} placeholder="418-123-4567" data-testid="jumelage-coord-cell" /></Champ>
                  <Champ label="Courriel" daj={false}><input value={coord.courriel || ""} maxLength={100} onChange={e => up("courriel", e.target.value)} data-testid="jumelage-coord-courriel" /></Champ>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                <button className="rech-btn-secondary" onClick={() => setStep("recherche")} data-testid="jumelage-btn-retour">← Retour</button>
                <button className="rech-btn-primary" onClick={() => setConfirming(true)}
                        disabled={loading || !(coord.nom || "").trim()}
                        data-testid="jumelage-btn-executer">
                  {nbJum > 1 ? "Mettre à jour et jumeler" : "Mettre à jour les coordonnées"}
                </button>
              </div>

              {confirming && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
                              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}>
                  <div style={{ background: "#fff", border: "1px solid #808080", boxShadow: "3px 3px 8px rgba(0,0,0,0.3)",
                                width: 480, maxWidth: "90vw" }} data-testid="jumelage-confirm">
                    <div className="rech-titlebar"><span>Confirmation du jumelage</span></div>
                    <div style={{ padding: 14, fontSize: 12 }}>
                      <p style={{ marginTop: 0 }}>Voici les informations qui seront conservées :</p>
                      <table style={{ fontSize: 12, lineHeight: 1.7 }}>
                        <tbody>
                          <tr><td style={{ paddingRight: 12, color: "#555" }}>No client</td><td><strong>{coord.noclient}</strong></td></tr>
                          <tr><td style={{ paddingRight: 12, color: "#555" }}>Nom, prénom</td><td>{coord.nom} {coord.prenom}</td></tr>
                          <tr><td style={{ paddingRight: 12, color: "#555" }}>Date naissance</td><td>{coord.date_naissance}</td></tr>
                          <tr><td style={{ paddingRight: 12, color: "#555" }}>N.A.S.</td><td>{coord.nas}</td></tr>
                          <tr><td style={{ paddingRight: 12, color: "#555" }}>Adresse</td><td>{coord.adresse}, {coord.ville} {coord.code_postal}</td></tr>
                        </tbody>
                      </table>
                      {nbJum > 1 && (
                        <p style={{ color: "#a00" }}>
                          Les clients suivants seront fusionnés puis <strong>supprimés</strong> :{" "}
                          {jumeles.filter(j => j.noclient !== coord.noclient).map(j => j.noclient).join(", ")}
                        </p>
                      )}
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                        <button className="rech-btn-secondary" onClick={() => setConfirming(false)} data-testid="jumelage-confirm-annuler">Annuler</button>
                        <button className="rech-btn-primary" onClick={executer} data-testid="jumelage-confirm-ok">Confirmer</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "fusion" && step === "resultat" && resultat && (
            <>
              <div style={{ fontWeight: 600, marginBottom: 6 }} data-testid="jumelage-resultat-titre">
                {resultat.type_operation === "JUMELAGE" ? "Jumelage effectué" : "Mise à jour effectuée"} — journal #{resultat.log_id}
              </div>
              {resultat.coordonnees && (
                <div style={{ background: "#eef7ee", border: "1px solid #b8dcb8", padding: "8px 12px",
                              marginBottom: 8, fontSize: 12, lineHeight: 1.7 }}
                     data-testid="jumelage-resultat-coordonnees">
                  <strong>Coordonnées enregistrées pour {resultat.coordonnees.prenom} {resultat.coordonnees.nom} ({resultat.noclient_conserve})</strong>
                  <div>
                    {resultat.coordonnees.adresse || "—"}, {resultat.coordonnees.ville || "—"}
                    {resultat.coordonnees.province ? ` (${resultat.coordonnees.province})` : ""} {resultat.coordonnees.code_postal || ""}
                  </div>
                  <div>
                    Tél. rés. : {resultat.coordonnees.tel_res || "—"} · Bur. : {resultat.coordonnees.tel_bur || "—"} · Cell. : {resultat.coordonnees.cellulaire || "—"}
                    {resultat.coordonnees.courriel ? ` · ${resultat.coordonnees.courriel}` : ""}
                  </div>
                </div>
              )}
              <div className="rech-table-wrap" style={{ maxHeight: 380 }}>
                <table className="rech-table" data-testid="jumelage-resultat-details">
                  <thead>
                    <tr><th>Table</th><th>Action</th><th>No client traité</th><th>Lignes</th><th>Heure</th></tr>
                  </thead>
                  <tbody>
                    {resultat.details.map((d, i) => (
                      <tr key={i}>
                        <td>{d.nom_table}</td><td>{d.type_action}</td>
                        <td>{d.noclient_source || ""}</td><td>{d.nb_lignes}</td><td>{d.date_action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                <button className="rech-btn-secondary"
                        onClick={() => { setStep("recherche"); setResultat(null);
                                         if (nom.trim() || prenom.trim()) handleSearch(); }}
                        data-testid="jumelage-btn-nouveau">Nouveau jumelage</button>
                <button className="rech-btn-primary" onClick={onClose} data-testid="jumelage-btn-terminer">Terminer</button>
              </div>
            </>
          )}

          {tab === "journal" && !logDetail && (
            <div className="rech-table-wrap" style={{ maxHeight: 420 }}>
              <table className="rech-table" data-testid="jumelage-logs">
                <thead>
                  <tr><th>#</th><th>Date</th><th>Utilisateur</th><th>Opération</th>
                      <th>Client conservé</th><th>Nom</th><th>Fusionnés</th><th>Statut</th></tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} onDoubleClick={() => ouvrirLog(l.id)} onClick={() => ouvrirLog(l.id)}
                        style={{ cursor: "pointer" }} data-testid={`jumelage-log-row-${l.id}`}>
                      <td>{l.id}</td><td>{l.date_debut}</td><td>{l.utilisateur}</td>
                      <td>{l.type_operation}</td><td>{l.noclient_conserve}</td>
                      <td>{l.prenom_conserve} {l.nom_conserve}</td>
                      <td>{l.nb_clients_fusionnes}</td>
                      <td style={{ color: l.statut === "SUCCES" ? "#1a7a1a" : "#a00", fontWeight: 600 }}>{l.statut}</td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr><td colSpan={8} style={{ color: "#666" }}>Aucun jumelage enregistré.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "journal" && logDetail && (
            <>
              <div style={{ fontSize: 12, marginBottom: 8 }} data-testid="jumelage-log-detail-header">
                <strong>Journal #{logDetail.id}</strong> — {logDetail.type_operation} par {logDetail.utilisateur} le {logDetail.date_debut}
                {" — "}client conservé <strong>{logDetail.noclient_conserve}</strong> ({logDetail.prenom_conserve} {logDetail.nom_conserve})
                {logDetail.clients_fusionnes.length > 0 && <> — fusionnés : {logDetail.clients_fusionnes.join(", ")}</>}
                {logDetail.statut !== "SUCCES" && (
                  <div style={{ color: "#a00" }}>ÉCHEC : {logDetail.message_erreur}</div>
                )}
              </div>
              <div className="rech-table-wrap" style={{ maxHeight: 380 }}>
                <table className="rech-table" data-testid="jumelage-log-detail-table">
                  <thead>
                    <tr><th>Table</th><th>Action</th><th>No client traité</th><th>Lignes</th><th>Heure</th></tr>
                  </thead>
                  <tbody>
                    {logDetail.details.map((d, i) => (
                      <tr key={i}>
                        <td>{d.nom_table}</td><td>{d.type_action}</td>
                        <td>{d.noclient_source || ""}</td><td>{d.nb_lignes}</td><td>{d.date_action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 10 }}>
                <button className="rech-btn-secondary" onClick={() => setLogDetail(null)} data-testid="jumelage-log-detail-retour">← Retour au journal</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
